import clsx from 'clsx'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import IconButton from 'LuxKit/Button/IconButton'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import Divider from 'LuxKit/Divider'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import type { WeddingVenueInspiration } from 'weddings/hooks/useVenuesList'
import WeddingVenueSeason from './WeddingVenueSeason'

const ITEMS_PER_ROW = 5

const ItemsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(8)};
  grid-auto-flow: column;
  grid-template-rows: repeat(${ITEMS_PER_ROW}, auto);

  &.two-columns {
    grid-template-columns: repeat(2, 1fr);
  }
`

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0;
  inset-inline-end: 0;
  margin: ${rem(16)};
  z-index: ${zIndex.floatingBookmarkButton};
`

const imageProps: ImageParams = {
  mobileAspectRatio: '25:16',
  tabletAspectRatio: '9:4',
  quality: 'good',
}

interface Props {
  onRequestQuoteClick: () => void
  venueInspirationOption: WeddingVenueInspiration
}

function WeddingVenueInspirationModal(props: Props) {
  const { onRequestQuoteClick, venueInspirationOption } = props
  const modalElement = useModalElementContext()

  const intl = useIntl()

  const onClose = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const onRequestFreeQuoteClick = useCallback(() => {
    onRequestQuoteClick()
    onClose()
  }, [onRequestQuoteClick, onClose])

  return (
    <ModalBase size="L" onClose={onClose}>
      <ModalBody>
        <div>
          <CloseButton kind="primary" variant="ghost" size="small" shape="circle" onClick={onClose}>
            <LineTimesIcon />
          </CloseButton>
        </div>
        <ImageGallery
          size="small"
          images={venueInspirationOption.images}
          showImageTitles={false}
          imageParams={imageProps}
        />
        <ModalContent>
          <Group direction="vertical" gap={28}>
            <Group direction="vertical" gap={12}>
              <Group direction="vertical">
                <BodyText variant="medium">{venueInspirationOption.location}</BodyText>
                <Heading variant="heading3">{venueInspirationOption.title}</Heading>
              </Group>
              {!!venueInspirationOption.rating && <OfferRating rating={venueInspirationOption.rating} variant="xs" />}
              <BodyText variant="medium" colour="light-foreground-alt">{venueInspirationOption.description}</BodyText>
            </Group>
            <Group direction="vertical" gap={8}>
              <Divider kind="primary" direction="horizontal" />
              <Group direction="vertical" gap={2} desktopDirection="horizontal" desktopGap={12}>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <LineUsersAltIcon size="S" colour="light-foreground-alt" />
                  <Group direction="horizontal" verticalAlign="center" gap={4}>
                    <BodyText variant="small" weight="bold" colour="light-foreground-alt">
                      {intl.formatMessage({
                        defaultMessage: 'Guest capacity:',
                        id: 'nqGq2NUx3zw1jtiIrF9n',
                        description: 'Guest capacity label for the inspiration tile in the "Venue inspirations" section on weddings landing page.',
                      })}
                    </BodyText>
                    <BodyText variant="small" colour="light-foreground-alt">{venueInspirationOption.guestCapacity}</BodyText>
                  </Group>
                </Group>
                <CSSBreakpoint min="desktop">
                  {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <BodyText variant="small">·</BodyText>
                </CSSBreakpoint>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <LineCalendarCheckIcon size="S" colour="light-foreground-alt" />
                  <Group direction="horizontal" verticalAlign="center" gap={4}>
                    <BodyText variant="small" weight="bold" colour="light-foreground-alt">
                      {intl.formatMessage({
                        defaultMessage: 'Best in:',
                        id: 'Rya5rrXaD6-qT7qa7Tyl',
                        description: 'Label for the best season for the inspiration tile in the "Venue inspirations" section on weddings landing page.',
                      })}
                    </BodyText>
                    <WeddingVenueSeason
                      season={venueInspirationOption.season}
                      seasonHint={venueInspirationOption.seasonHint}
                      variant="small"
                    />
                  </Group>
                </Group>
                <CSSBreakpoint min="desktop">
                  {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <BodyText variant="small">·</BodyText>
                </CSSBreakpoint>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <LineUsdCircleIcon size="S" colour="light-foreground-alt" />
                  <Group direction="horizontal" verticalAlign="center" gap={4}>
                    <BodyText variant="small" colour="light-foreground-alt">
                      {intl.formatMessage(
                        {
                          defaultMessage: '<bold>Pricing:</bold> quote on request',
                          id: 'chJyqlDmVoQO5Ef-If0s',
                          description: 'Label for the pricing for the inspiration tile in the "Venue inspirations" section on weddings landing page.',
                        },
                        {
                          bold: (chunks) => <b>{chunks}</b>,
                        },
                      )}
                    </BodyText>
                  </Group>
                </Group>
              </Group>
              <Divider kind="primary" direction="horizontal" />
            </Group>
            <Group direction="vertical" gap={20}>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: '2NC7BF5nzcGXYhrWpTN0',
                  defaultMessage: 'Why couples love this venue',
                  description: 'Header for the highlights section in the venue inspiration modal on the weddings landing page',
                })}
              </Heading>
              <ItemsContainer className={clsx({ 'two-columns': venueInspirationOption.highlights.length > ITEMS_PER_ROW })}>
                {venueInspirationOption.highlights.map((highlight, index) => (
                  <Group key={index.toString()} direction="horizontal" gap={8}>
                    <LineCheckIcon size="S" colour="light-foreground-alt" />
                    <BodyText variant="medium" colour="light-foreground-alt">{highlight}</BodyText>
                  </Group>
                ))}
              </ItemsContainer>
            </Group>
            {!!venueInspirationOption.amenities.length && <Group direction="vertical" gap={20}>
              <Heading variant="heading5">
                {intl.formatMessage({
                  id: '5jOvx4ncviuH3EFvz5j4',
                  defaultMessage: 'Amenities',
                  description: 'Header for the amenities section in the venue inspiration modal on the weddings landing page',
                })}
              </Heading>
              <ItemsContainer className={clsx({ 'two-columns': venueInspirationOption.amenities.length > ITEMS_PER_ROW })}>
                {venueInspirationOption.amenities.map((amenity, index) => (
                  <Group key={index.toString()} direction="horizontal" gap={8}>
                    <LineCheckIcon size="S" colour="light-foreground-alt" />
                    <BodyText variant="medium" colour="light-foreground-alt" format="titlecase">{amenity}</BodyText>
                  </Group>
                ))}
              </ItemsContainer>
            </Group>}
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          shape: 'round',
          onClick: onRequestFreeQuoteClick,
          children: intl.formatMessage({
            id: 'z-wZ8RLjpm-nj_Qhs29r',
            defaultMessage: 'Get a free quote',
            description: 'CTA to get a free quote in the venue inspiration modal on the weddings lading page',
          }),
        }}
      />
    </ModalBase>
  )
}

export default WeddingVenueInspirationModal
